import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { copy } from "../copy";
import Icon from "../components/Icon";
import { adminApi, type CreatedContest, type Options } from "../admin/api";
import {
  AdminPageHeader,
  Card,
  COVER_TYPES,
  contestBody,
  ContestFields,
  coverOK,
  EMPTY_CONTEST_FORM,
  FILE_TYPES,
  FilePicker,
  sampleOK,
  SummaryRow,
  type ContestFormValues,
} from "../admin/ContestForm";
import { ghostButton, LoadError, Loading, primaryButton, type Load } from "../admin/ui";
import { putFile } from "../contest/upload";
import { AdminGate } from "./Admin";

type Phase = { kind: "editing" } | { kind: "creating" } | { kind: "uploading"; text: string } | { kind: "done"; contest: CreatedContest };

// The Control Room's New contest page. It creates a free-entry contest (the
// market pool is the prize), then uploads the cover and sample files to the
// presigned URLs the server hands back.
export default function NewContest() {
  return (
    <AdminGate title={copy.admin.newContest.title}>
      <NewContestForm />
    </AdminGate>
  );
}

function NewContestForm() {
  const t = copy.admin.newContest;
  const [options, setOptions] = useState<Load<Options>>({ status: "idle" });
  const [form, setForm] = useState<ContestFormValues>(EMPTY_CONTEST_FORM);
  const [cover, setCover] = useState<File | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [phase, setPhase] = useState<Phase>({ kind: "editing" });
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState<File[]>([]);

  const loadOptions = async () => {
    setOptions({ status: "loading" });
    const res = await adminApi.options();
    setOptions(res.ok ? { status: "ready", data: res.data } : { status: "error" });
  };
  useEffect(() => {
    void loadOptions();
  }, []);

  const set = <K extends keyof ContestFormValues>(key: K, value: ContestFormValues[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // upload presigns and PUTs each file in turn and returns the ones that
  // did not make it. The contest already exists by then.
  const upload = async (contestID: string, todo: File[]): Promise<File[]> => {
    const missed: File[] = [];
    for (const file of todo) {
      setPhase({ kind: "uploading", text: t.uploading(file.name, 0) });
      const res = await adminApi.presignUpload(contestID, file.name);
      const ok =
        res.ok &&
        (await putFile(
          res.data.url,
          file,
          res.data.content_type,
          (pct) => setPhase({ kind: "uploading", text: t.uploading(file.name, pct) }),
          res.data.headers,
        ));
      if (!ok) missed.push(file);
    }
    return missed;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (phase.kind !== "editing") return;
    setError(null);
    setPhase({ kind: "creating" });
    const res = await adminApi.createContest(contestBody(form));
    if (!res.ok) {
      setError(res.error);
      setPhase({ kind: "editing" });
      return;
    }
    const todo = [...(cover ? [cover] : []), ...files];
    setFailed(todo.length > 0 ? await upload(res.data.id, todo) : []);
    setPhase({ kind: "done", contest: res.data });
  };

  const retry = async (contest: CreatedContest) => {
    const missed = await upload(contest.id, failed);
    setFailed(missed);
    setPhase({ kind: "done", contest });
  };

  const reset = () => {
    setForm(EMPTY_CONTEST_FORM);
    setCover(null);
    setFiles([]);
    setFailed([]);
    setError(null);
    setPhase({ kind: "editing" });
  };

  const header = <AdminPageHeader heading={t.heading} />;

  if (phase.kind === "done") {
    const c = phase.contest;
    return (
      <>
        {header}
        <section
          role="status"
          className="mx-auto mt-10 max-w-xl rounded-2xl border border-ground-edge bg-ground-raised p-8 text-center md:p-10"
        >
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-money/15 text-2xl text-money">
            <Icon name="checkboxCircle" />
          </span>
          <h2 className="mt-5 font-headline text-3xl uppercase text-chrome">{t.created}</h2>
          <p className="mt-3 text-sm text-chrome-dim">{t.createdBody(c.title, c.published)}</p>
          {failed.length > 0 && (
            <div role="alert" className="mt-6 rounded-xl border border-alert/50 bg-alert/10 px-4 py-3 text-left text-[13px] text-chrome">
              {failed.map((f) => (
                <p key={f.name}>{t.uploadFailed(f.name)}</p>
              ))}
              <button type="button" onClick={() => void retry(c)} className={`${ghostButton} mt-3`}>
                <Icon name="refresh" className="text-sm" />
                {t.retry}
              </button>
            </div>
          )}
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            {c.url && (
              // Go serves the contest page; a full page load.
              <a href={c.url} className={primaryButton}>
                <Icon name="externalLink" className="text-sm" />
                {t.view}
              </a>
            )}
            <Link to={`/admin/contests/${c.id}`} className={ghostButton}>
              <Icon name="coins" className="text-sm" />
              {t.setUpMarket}
            </Link>
            <button type="button" onClick={reset} className={ghostButton}>
              <Icon name="add" className="text-sm" />
              {t.another}
            </button>
            <Link to="/admin" className={ghostButton}>
              {t.back}
            </Link>
          </div>
        </section>
      </>
    );
  }

  if (options.status === "error") {
    return (
      <>
        {header}
        <div className="mt-7 rounded-2xl border border-ground-edge bg-ground-raised p-6">
          <LoadError onRetry={() => void loadOptions()} />
        </div>
      </>
    );
  }
  if (options.status !== "ready") {
    return (
      <>
        {header}
        <Loading />
      </>
    );
  }

  const opts = options.data;
  const busy = phase.kind !== "editing";
  const seats = Number(form.seats);
  return (
    <>
      {header}

      {error && (
        <div role="alert" className="mt-6 rounded-xl border border-alert/50 bg-alert/10 px-4 py-3 text-[13px] text-chrome">
          {error}
        </div>
      )}

      <form onSubmit={(e) => void submit(e)} className="mt-7 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
        <div className="min-w-0 space-y-6">
          <ContestFields form={form} set={set} options={opts} showPublish />

          <Card title={t.media} icon="uploadCloud">
            <FilePicker
              id="new-cover"
              label={t.cover}
              rules={t.coverRules}
              accept={COVER_TYPES.join(",")}
              files={cover ? [cover] : []}
              onPick={(picked) => {
                const f = picked[0];
                if (!f) return;
                if (!coverOK(f)) {
                  setError(t.badCover);
                  return;
                }
                setError(null);
                setCover(f);
              }}
              onRemove={() => setCover(null)}
            />
            <FilePicker
              id="new-files"
              label={t.files}
              rules={t.filesRules}
              accept={FILE_TYPES.join(",")}
              multiple
              files={files}
              onPick={(picked) => {
                const bad = picked.find((f) => !sampleOK(f));
                if (bad) {
                  setError(t.badFile(bad.name));
                  return;
                }
                setError(null);
                setFiles((prev) => [...prev, ...picked.filter((f) => !prev.some((p) => p.name === f.name))]);
              }}
              onRemove={(name) => setFiles((prev) => prev.filter((f) => f.name !== name))}
            />
          </Card>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-ground-edge bg-ground-raised p-5 md:p-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">{t.summary}</p>
            <p
              className={`mt-3 break-words font-headline text-2xl uppercase leading-tight ${form.name.trim() ? "text-chrome" : "text-chrome-dim/50"}`}
            >
              {form.name.trim() || t.namePlaceholder}
            </p>
            <p className="mt-1 text-[13px] text-chrome-dim">
              {[form.challenge, form.genre].filter(Boolean).join(" · ") || " "}
            </p>
            <dl className="mt-5 divide-y divide-ground-edge border-y border-ground-edge text-[13px]">
              <SummaryRow label={t.entry} value={<span className="font-bold text-money">{t.free}</span>} />
              <SummaryRow label={t.prize} value={<span className="text-gold">{t.prizeValue}</span>} />
              <SummaryRow label={t.seats} value={<span className="bf-tabular">{Number.isFinite(seats) ? seats : "–"}</span>} />
              <SummaryRow label={t.winners} value={<span className="bf-tabular">{t.oneWinner}</span>} />
              <SummaryRow label={t.duration} value={form.duration || "–"} />
              <SummaryRow label={t.voting} value={t.votingOptions[form.voting]} />
              <SummaryRow label={t.starts} value={t.startsNow} />
            </dl>
            <p className="mt-4 text-[12px] leading-relaxed text-chrome-dim">{t.summaryNote}</p>
            <button type="submit" disabled={busy} className={`${primaryButton} mt-6 h-12 w-full text-[12px]`}>
              {phase.kind === "creating" ? t.creating : phase.kind === "uploading" ? phase.text : t.submit}
            </button>
          </div>
        </aside>
      </form>
    </>
  );
}
